import React, { useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useTranslation } from 'react-i18next';

const formatMoney = (v) => new Intl.NumberFormat('vi-VN').format(v);

export default function SubscriptionPlansDialog({
  open,
  onClose,
  user,
  pricingPlans,
  loading,
  onSelectPlan,
  onCancelSubscription,
  onActivateFree
}) {
  const { t } = useTranslation();
  const [hoveredPlan, setHoveredPlan] = useState(null);

  // Parse user subscription status
  const plan = user?.planType ? String(user.planType) : '';
  const badge = user?.planBadge ? String(user.planBadge) : '';
  const hasSelectedFree = !!user?.hasSelectedFree;
  const subscribedUntil = user?.subscribedUntil ? new Date(user.subscribedUntil) : null;

  const endOfDay = (d) => {
    if (!d || isNaN(d.getTime())) return null;
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const end = subscribedUntil ? endOfDay(subscribedUntil) : null;
  const inFuture = !!(end && end.getTime() >= Date.now());
  const msLeft = end ? (end.getTime() - Date.now()) : null;
  const daysLeft = (typeof msLeft === 'number') ? Math.ceil(msLeft / (1000 * 60 * 60 * 24)) : null;

  const isPaid = plan === 'PAID_MONTHLY' || plan === 'PAID_YEARLY';
  const paidActive = isPaid && inFuture;
  const isCancelled = paidActive && !!user?.cancelledAt;
  const trialActive = (badge === 'Trial' && inFuture) || (plan === 'FREE' && inFuture);
  const hadAnyWindow = !!subscribedUntil;
  const isCurrentlyFree = plan === 'FREE';
  const canActivateFree = !paidActive && !trialActive && !hasSelectedFree && !hadAnyWindow && !isCurrentlyFree;

  // Get prices from backend
  const getPrice = (code) => {
    const p = Array.isArray(pricingPlans) ? pricingPlans.find(pl => pl.code === code) : null;
    return p?.priceVnd || (code === 'MONTHLY' ? 50000 : 480000);
  };

  const monthlyPrice = getPrice('MONTHLY');
  const yearlyPrice = getPrice('YEARLY');
  const yearlySavings = Math.round(((monthlyPrice * 12) - yearlyPrice) / (monthlyPrice * 12) * 100);

  // Check if a plan is current
  const isCurrentPlan = (planCode) => {
    if (planCode === 'FREE') return isCurrentlyFree && inFuture;
    if (planCode === 'MONTHLY') return plan === 'PAID_MONTHLY' && inFuture;
    if (planCode === 'YEARLY') return plan === 'PAID_YEARLY' && inFuture;
    return false;
  };

  const isExpiredPlan = (planCode) => {
    if (planCode === 'MONTHLY') return plan === 'PAID_MONTHLY' && !inFuture;
    if (planCode === 'YEARLY') return plan === 'PAID_YEARLY' && !inFuture;
    return false;
  };

  const FEATURES = [
    { key: 'subscription.feature.unlimitedAccess', fallback: 'Unlimited access to all content' },
    { key: 'subscription.feature.allSubjects', fallback: 'All subjects and lessons' },
    { key: 'subscription.feature.practiceTests', fallback: 'Practice tests and quizzes' },
    { key: 'subscription.feature.progressTracking', fallback: 'Progress tracking' },
    { key: 'subscription.feature.prioritySupport', fallback: 'Priority support' }
  ];

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 3,
        position: 'relative'
      }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{
            width: 56,
            height: 56,
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <WorkspacePremiumIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {t('subscription.plansTitle', 'Subscription Plans')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t('subscription.plansIntro', 'Choose a plan that fits you')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (<>
        {/* Free plan banner */}
        {isCurrentlyFree && inFuture && daysLeft !== null && (
          <Box sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('subscription.freePlanDaysLeft', 'Gói Miễn phí — còn {{count}} ngày', { count: daysLeft })}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t('subscription.upgradeToUnlock', 'Nâng cấp để mở khóa tất cả tính năng')}
            </Typography>
          </Box>
        )}

        {/* Expiry Warning */}
        {paidActive && daysLeft !== null && daysLeft <= 7 && (
          <Box sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: 'warning.light',
            color: 'warning.dark',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <RocketLaunchIcon />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {daysLeft <= 1
                ? t('subscription.endsToday', 'Your plan ends today. Please renew to avoid interruption.')
                : t('subscription.endsInDays', 'Your plan ends in {{count}} day(s)', { count: daysLeft })
              }
            </Typography>
          </Box>
        )}

        {/* Cancelled Status */}
        {isCancelled && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            {t('subscription.cancelledUntil', 'Cancelled — Active until {{date}}', {
              date: subscribedUntil ? subscribedUntil.toLocaleDateString(undefined) : ''
            })}
          </Alert>
        )}

        {/* Plan Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
          {/* Free Plan */}
          <Card
            onMouseEnter={() => setHoveredPlan('FREE')}
            onMouseLeave={() => setHoveredPlan(null)}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: '2px solid',
              borderColor: isCurrentPlan('FREE') ? 'success.main' : (hoveredPlan === 'FREE' ? 'primary.light' : 'divider'),
              transition: 'all 0.2s ease',
              transform: hoveredPlan === 'FREE' ? 'translateY(-4px)' : 'none',
              boxShadow: hoveredPlan === 'FREE' ? 6 : 1
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.200' }}>
                        <LockOpenIcon sx={{ color: 'grey.600' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('subscription.freeTitle', 'Free')}
                      </Typography>
                    </Stack>
                    {isCurrentPlan('FREE') && (
                      <Chip
                        size="small"
                        color="success"
                        label={t('subscription.currentPlan', 'Current')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                  {t('subscription.freeDesc', 'Basic access with limited features')}
                </Typography>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    0 <Typography component="span" variant="body2" color="text.secondary">VND</Typography>
                  </Typography>
                </Box>
                {canActivateFree && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={onActivateFree}
                    sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                  >
                    {t('subscription.activateFree', 'Activate Free')}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Monthly Plan */}
          <Card
            onMouseEnter={() => setHoveredPlan('MONTHLY')}
            onMouseLeave={() => setHoveredPlan(null)}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: '2px solid',
              borderColor: isCurrentPlan('MONTHLY') ? 'primary.main' : (hoveredPlan === 'MONTHLY' ? 'primary.light' : 'divider'),
              transition: 'all 0.2s ease',
              transform: hoveredPlan === 'MONTHLY' ? 'translateY(-4px)' : 'none',
              boxShadow: hoveredPlan === 'MONTHLY' ? 6 : 1
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha('#1976d2', 0.1) }}>
                        <CalendarMonthIcon sx={{ color: 'primary.main' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('subscription.monthly', 'Monthly')}
                      </Typography>
                    </Stack>
                    {isCurrentPlan('MONTHLY') && (
                      <Chip
                        size="small"
                        color="success"
                        label={t('subscription.currentPlan', 'Current')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isExpiredPlan('MONTHLY') && (
                      <Chip
                        size="small"
                        color="default"
                        label={t('subscription.expired', 'Expired')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                  {t('subscription.monthlyDesc', 'Flexible month-to-month plan')}
                </Typography>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {formatMoney(monthlyPrice)}
                    <Typography component="span" variant="body2" color="text.secondary"> VND/{t('subscription.perMonth', 'month')}</Typography>
                  </Typography>
                </Box>
                <Button
                  variant={isCurrentPlan('MONTHLY') ? 'outlined' : 'contained'}
                  fullWidth
                  disabled={isCurrentPlan('MONTHLY')}
                  onClick={() => onSelectPlan?.('PAID_MONTHLY')}
                  sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                >
                  {isCurrentPlan('MONTHLY')
                    ? t('subscription.currentPlan', 'Current Plan')
                    : t('subscription.selectMonthly', 'Select Monthly')
                  }
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Yearly Plan - Best Value */}
          <Card
            onMouseEnter={() => setHoveredPlan('YEARLY')}
            onMouseLeave={() => setHoveredPlan(null)}
            sx={{
              flex: 1,
              borderRadius: 3,
              border: '2px solid',
              borderColor: isCurrentPlan('YEARLY') ? 'warning.main' : (hoveredPlan === 'YEARLY' ? 'warning.light' : 'warning.main'),
              transition: 'all 0.2s ease',
              transform: hoveredPlan === 'YEARLY' ? 'translateY(-4px)' : 'none',
              boxShadow: hoveredPlan === 'YEARLY' ? 8 : 3,
              position: 'relative',
              overflow: 'visible'
            }}
          >
            {/* Best Value Badge */}
            <Chip
              icon={<StarIcon />}
              label={t('subscription.bestValue', 'Best Value')}
              color="warning"
              size="small"
              sx={{
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                fontWeight: 700,
                px: 1
              }}
            />
            <CardContent sx={{ p: 2.5, pt: 3.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{
                        width: 36,
                        height: 36,
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      }}>
                        <AllInclusiveIcon sx={{ color: 'white' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('subscription.yearly', 'Yearly')}
                      </Typography>
                    </Stack>
                    {isCurrentPlan('YEARLY') && (
                      <Chip
                        size="small"
                        color="success"
                        label={t('subscription.currentPlan', 'Current')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isExpiredPlan('YEARLY') && (
                      <Chip
                        size="small"
                        color="default"
                        label={t('subscription.expired', 'Expired')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                  {t('subscription.yearlyDesc', 'Best value for committed learners')}
                </Typography>
                <Box>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main' }}>
                      {formatMoney(yearlyPrice)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      VND/{t('subscription.perYear', 'year')}
                    </Typography>
                  </Stack>
                  {yearlySavings > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                        {formatMoney(monthlyPrice * 12)} VND
                      </Typography>
                      <Chip
                        size="small"
                        color="success"
                        label={t('subscription.savePercent', 'Save {{percent}}%', { percent: yearlySavings })}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    </Stack>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  disabled={isCurrentPlan('YEARLY')}
                  onClick={() => onSelectPlan?.('PAID_YEARLY')}
                  sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                >
                  {isCurrentPlan('YEARLY')
                    ? t('subscription.currentPlan', 'Current Plan')
                    : t('subscription.selectYearly', 'Select Yearly')
                  }
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Features List */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('subscription.featuresNote', 'Premium features include')}:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.5}>
            {FEATURES.map((feature, i) => (
              <Chip
                key={i}
                icon={<CheckCircleIcon sx={{ color: 'success.main' }} />}
                label={t(feature.key, feature.fallback)}
                variant="outlined"
                sx={{
                  borderColor: 'success.light',
                  bgcolor: alpha('#10b981', 0.05),
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            ))}
          </Stack>
        </Box>
        </>)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        {paidActive && !isCancelled && (
          <Button
            variant="outlined"
            onClick={onCancelSubscription}
            sx={{
              mr: 'auto',
              borderRadius: 3,
              px: 2.5,
              py: 0.8,
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'none',
              color: '#ef4444',
              borderColor: 'rgba(239,68,68,0.35)',
              background: 'rgba(239,68,68,0.04)',
              transition: 'all 0.25s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
                borderColor: 'transparent',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
              },
            }}
          >
            {t('subscription.cancelSubscription', 'Cancel Subscription')}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
